import axios from 'axios'
import { useAuth } from 'hooks/useAuth'
import { FormEvent, useState } from 'react'
import { LuActivity, LuChartColumn, LuTriangleAlert, LuUsers } from 'react-icons/lu'
import { Spinner } from 'components/ui/States'
import { Footer } from 'components/Footer'

/** Only a 400/401 means bad credentials; anything else is a connection or server problem. */
const loginErrorMessage = (error: unknown) => {
    if (axios.isAxiosError(error)) {
        const status = error.response?.status
        if (status === 400 || status === 401) return '帳號或密碼錯誤'
        if (!error.response) return '無法連線至伺服器，請檢查網絡後再試'
    }
    return '登入失敗，請稍後再試'
}

const HIGHLIGHTS = [
    { icon: LuChartColumn, text: '每月運動時數及次數趨勢' },
    { icon: LuUsers, text: '各年級、班級及學生參與分析' },
]

export const LoginPage = () => {
    const { login } = useAuth()
    const [name, setName] = useState('')
    const [password, setPassword] = useState('')

    const onSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (login.isPending) return
        login.mutate({ identifier: name.trim(), password })
    }

    return <div className='flex min-h-screen flex-col bg-canvas'>
        <div className='grid flex-1 lg:grid-cols-2'>
            {/* Brand panel (desktop only) */}
            <aside className='hidden flex-col justify-between bg-primary-50 p-12 lg:flex'>
                <div className='flex items-center gap-3'>
                    <span className='grid h-9 w-9 place-items-center rounded-lg bg-primary text-white'>
                        <LuActivity aria-hidden className='h-5 w-5' />
                    </span>
                    <span className='text-[17px] font-semibold tracking-tight text-ink'>Intelligent Playground</span>
                </div>
                <div className='max-w-lg'>
                    <h1 className='text-display text-ink xl:text-display-md'>讓每一次運動<br />都被看見</h1>
                    <p className='mt-4 text-base text-ink-muted'>智能運動場數據平台，助學校掌握學生的運動習慣，安排更合適的體育課堂。</p>
                    <ul className='mt-8 space-y-3'>
                        {HIGHLIGHTS.map(({ icon: Icon, text }) => <li key={text} className='flex items-center gap-3 text-sm font-medium text-ink'>
                            <span className='grid h-8 w-8 place-items-center rounded-full bg-surface text-primary shadow-rim'>
                                <Icon aria-hidden className='h-4 w-4' />
                            </span>
                            {text}
                        </li>)}
                    </ul>
                </div>
                <p className='text-xs text-ink-faint'>© {new Date().getFullYear()} Intelligent Playground</p>
            </aside>

            {/* Form */}
            <main className='flex items-center justify-center px-4 py-12'>
                <div className='w-full max-w-sm'>
                    <div className='mb-8 flex items-center justify-center gap-3 lg:hidden'>
                        <span className='grid h-9 w-9 place-items-center rounded-lg bg-primary text-white'>
                            <LuActivity aria-hidden className='h-5 w-5' />
                        </span>
                        <span className='text-[17px] font-semibold tracking-tight text-ink'>Intelligent Playground</span>
                    </div>
                    <form onSubmit={onSubmit} className='rounded-3xl bg-surface p-6 shadow-float sm:p-8' noValidate>
                        <h2 className='text-h1 text-ink'>登入</h2>
                        <p className='mt-1 text-sm text-ink-muted'>請使用學校管理員帳號登入儀表板</p>

                        {login.isError && <div role='alert' className='mt-5 flex items-start gap-2 rounded-xl bg-danger-50 px-4 py-3 text-sm font-medium text-danger'>
                            <LuTriangleAlert aria-hidden className='mt-0.5 h-4 w-4 shrink-0' />
                            {loginErrorMessage(login.error)}
                        </div>}

                        <div className='mt-6 space-y-4'>
                            <label className='flex flex-col gap-1.5'>
                                <span className='text-sm font-medium text-ink-muted'>帳號</span>
                                <input
                                    type='text'
                                    name='username'
                                    autoComplete='username'
                                    placeholder='請輸入帳號'
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className='input h-12 rounded-xl text-base placeholder:text-ink-faint' />
                            </label>
                            <label className='flex flex-col gap-1.5'>
                                <span className='text-sm font-medium text-ink-muted'>密碼</span>
                                <input
                                    type='password'
                                    name='password'
                                    autoComplete='current-password'
                                    placeholder='請輸入密碼'
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className='input h-12 rounded-xl text-base placeholder:text-ink-faint' />
                            </label>
                        </div>

                        <button
                            type='submit'
                            disabled={login.isPending || !name || !password}
                            className='btn btn-primary mt-6 h-12 w-full rounded-xl px-6 text-base'>
                            {login.isPending && <Spinner tone='inverse' />}
                            {login.isPending ? '登入中…' : '登入'}
                        </button>
                    </form>
                </div>
            </main>
        </div>
        <Footer />
    </div>
}
